import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from './AuthContext.jsx'
import { authenticatedFetch } from '../utils/auth.js'

const SelectedStoreContext = createContext(null)

/** Map legacy saved preference strings to the current canonical store name after renames. */
function normalizePreferredStoreNameForMatching(name) {
  const t = name.trim().toLowerCase()
  if (!t) return name.trim()
  if (t === 'keene nh store (updated)' || (t.includes('keene nh store') && t.includes('updated'))) {
    return 'Shaws - Peterborough'
  }
  if (t === 'shaws - peterborough nh') {
    return 'Shaws - Peterborough'
  }
  return name.trim()
}

export function SelectedStoreProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const [selectedStore, setSelectedStore] = useState(null)
  const [stores, setStores] = useState([])
  const [storesLoading, setStoresLoading] = useState(true)
  const [storesError, setStoresError] = useState(null)
  const [defaultStoreId, setDefaultStoreId] = useState(null)

  // Seed from localStorage on mount (same tab); cross-tab uses `storage` below.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('selected_store') || 'null')
      if (saved?.id) {
        setSelectedStore({ id: saved.id, name: saved.name })
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    function onStorage(e) {
      if (e.key !== 'selected_store') return
      try {
        const next = JSON.parse(e.newValue || 'null')
        setSelectedStore(next?.id ? { id: next.id, name: next.name } : null)
      } catch {
        setSelectedStore(null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    function onLogout() {
      setSelectedStore(null)
      setDefaultStoreId(null)
    }
    window.addEventListener('userLoggedOut', onLogout)
    return () => window.removeEventListener('userLoggedOut', onLogout)
  }, [])

  const selectStore = useCallback((store) => {
    const next = store ? { id: store.id, name: store.name } : null
    setSelectedStore(next)
    try {
      if (next) {
        localStorage.setItem('selected_store', JSON.stringify(next))
      } else {
        localStorage.removeItem('selected_store')
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadStores() {
      setStoresLoading(true)
      setStoresError(null)
      try {
        const [storesRes, defaultRes] = await Promise.all([
          fetch('/grocery/stores'),
          fetch('/grocery/stores/default'),
        ])

        if (!storesRes.ok) {
          const errorText = await storesRes.text().catch(() => storesRes.statusText)
          console.error(`Failed to fetch stores: ${storesRes.status}`, errorText)
          if (!cancelled) {
            setStores([])
            setStoresError(`Could not load stores (${storesRes.status}).`)
          }
          return
        }

        if (!defaultRes.ok) {
          console.warn(`Failed to fetch default store: ${defaultRes.status}`)
        }

        const [storesJson, defaultJson] = await Promise.all([
          storesRes.json(),
          defaultRes.ok ? defaultRes.json() : { store: null },
        ])
        if (cancelled) return

        const fetchedStores = storesJson.stores || []
        setStores(fetchedStores)

        if (defaultJson?.store?.id) {
          setDefaultStoreId(defaultJson.store.id)
        } else {
          setDefaultStoreId(null)
        }

        let preferredFromApi = null
        if (isAuthenticated && fetchedStores.length > 0) {
          try {
            const prefsRes = await authenticatedFetch('/preferences/me', { headers: {} })
            if (prefsRes.ok) {
              const prefsData = await prefsRes.json().catch(() => ({}))
              const preferredName = prefsData.preferences?.shopping?.preferred_store
              if (typeof preferredName === 'string' && preferredName.trim()) {
                const needle = normalizePreferredStoreNameForMatching(preferredName).toLowerCase()
                const match = fetchedStores.find(
                  (s) =>
                    s.name &&
                    (s.name.toLowerCase() === needle ||
                      s.name.toLowerCase().includes(needle) ||
                      needle.includes(s.name.toLowerCase()))
                )
                if (match) {
                  preferredFromApi = { id: match.id, name: match.name }
                }
              }
            }
          } catch {
            // non-fatal
          }
        }
        if (cancelled) return

        const saved = JSON.parse(localStorage.getItem('selected_store') || 'null')
        let next = null
        if (preferredFromApi) {
          next = preferredFromApi
        } else if (saved && fetchedStores.find((s) => s.id === saved.id)) {
          next = { id: saved.id, name: saved.name }
        } else if (defaultJson?.store) {
          next = { id: defaultJson.store.id, name: defaultJson.store.name }
        } else if (fetchedStores.length > 0) {
          next = { id: fetchedStores[0].id, name: fetchedStores[0].name }
        }
        if (next) {
          setSelectedStore(next)
          try {
            localStorage.setItem('selected_store', JSON.stringify(next))
          } catch {
            // ignore
          }
        }
      } catch (err) {
        console.error('Error loading stores:', err)
        if (!cancelled) {
          setStores([])
          setStoresError(err?.message || 'Could not load stores.')
        }
      } finally {
        if (!cancelled) setStoresLoading(false)
      }
    }

    loadStores()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const value = useMemo(
    () => ({
      selectedStore,
      selectStore,
      stores,
      storesLoading,
      storesError,
      defaultStoreId,
    }),
    [selectedStore, selectStore, stores, storesLoading, storesError, defaultStoreId]
  )

  return <SelectedStoreContext.Provider value={value}>{children}</SelectedStoreContext.Provider>
}

export { SelectedStoreContext }
